/**
 * Settings shell route — Manager+ only.
 *
 * PRD addendum Apr-2026: small admin-style features (Tags, Audit
 * History, future picklist editors, etc.) used to each have their own
 * top-bar nav entry. That bloated the topbar and meant a small
 * surface like Tags occupied the same nav real estate as Deals or
 * Contacts. Moving them under a single "Settings" gear icon
 * de-clutters the top bar and groups configuration in one place.
 *
 * Layout:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │  Settings                                        │
 *   │  Configure tags, picklists, history.             │
 *   ├──────────────────────────────────────────────────┤
 *   │ ┌──────────┐  ┌─────────────────────────────────┐│
 *   │ │ • Tags   │  │  <Outlet />                     ││
 *   │ │   Audit  │  │                                 ││
 *   │ └──────────┘  └─────────────────────────────────┘│
 *   └──────────────────────────────────────────────────┘
 *
 * On phones the sidebar collapses to a horizontal pill-bar above the
 * content (much friendlier than a left rail at 360px). The active
 * link is always highlighted with the same accent treatment we use
 * across the app.
 *
 * The shell itself is role-gated to Manager+; child routes have
 * their own (stricter, e.g. Audit is admin-only) gate. Server-side
 * auth is the source of truth — these checks just keep reps from
 * staring at a spinner before the 403 redirect lands.
 */

import { Link, Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import {
  Cloud,
  Columns3,
  Database,
  History,
  ListChecks,
  MonitorSmartphone,
  Settings as SettingsIcon,
  ShieldCheck,
  Tag as TagIcon,
  Truck,
  UserRound,
  Warehouse,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

import { useAuthStore } from "../lib/auth";

interface SettingsNavEntry {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Min role to see this entry. Server enforces; this just hides
   *  the link so reps don't get a 403 on every click. */
  minRole: "manager" | "admin" | "owner";
}

const NAV_ENTRIES: SettingsNavEntry[] = [
  { to: "/settings/custom-fields", label: "Custom fields", icon: Columns3, minRole: "admin" },
  { to: "/settings/tags", label: "Tags", icon: TagIcon, minRole: "manager" },
  {
    to: "/settings/picklists",
    label: "Picklists",
    icon: ListChecks,
    minRole: "manager",
  },
  { to: "/settings/warehouses", label: "Warehouses", icon: Warehouse, minRole: "manager" },
  { to: "/settings/drivers", label: "Drivers", icon: UserRound, minRole: "manager" },
  { to: "/settings/transports", label: "Transport", icon: Truck, minRole: "manager" },
  { to: "/settings/audit", label: "History", icon: History, minRole: "admin" },
  { to: "/settings/roles", label: "Custom roles", icon: ShieldCheck, minRole: "admin" },
  {
    to: "/settings/data-recovery",
    label: "Data & recovery",
    icon: Database,
    minRole: "admin",
  },
  {
    to: "/settings/security",
    label: "Active sessions",
    icon: MonitorSmartphone,
    minRole: "admin",
  },
  {
    to: "/settings/google-drive",
    label: "Google Drive",
    icon: Cloud,
    minRole: "owner",
  },
];

export const Route = createFileRoute("/_app/settings")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    // Manager+ only at the shell level. Individual sub-pages re-check
    // for stricter gates (Audit is admin-only).
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: SettingsLayout,
});

function SettingsLayout() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const visibleEntries = NAV_ENTRIES.filter(
    (entry) =>
      entry.minRole === "manager" ||
      (entry.minRole === "admin" && isAdmin) ||
      (entry.minRole === "owner" && user?.is_owner),
  );

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <SettingsIcon className="size-5 opacity-70" aria-hidden />
          Settings
        </h1>
        <p className="text-sm opacity-60">
          Configure CRM and ERP reference data, security integrations, and activity history from one
          place.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-[200px_minmax(0,1fr)]">
        {/* Sidebar — on md+ this is a vertical rail; on phones it
            collapses to a horizontal scroll-strip of pill links. */}
        <aside aria-label="Settings sections">
          <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col">
            {visibleEntries.map((entry) => {
              const Icon = entry.icon;
              return (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm opacity-80 transition-colors hover:bg-black/5 hover:opacity-100 [&.active]:bg-[var(--accent)] [&.active]:font-medium [&.active]:text-white [&.active]:opacity-100 dark:hover:bg-white/5"
                  activeProps={{ className: "active" }}
                  activeOptions={{ exact: false }}
                >
                  <Icon className="size-4 opacity-70" aria-hidden />
                  <span>{entry.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Content — sub-routes mount here. Each sub-route brings its
            own page-level header so we don't re-emit "Settings" twice. */}
        <section className="min-w-0">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
